import { useMemo, useState } from 'react';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { ScreenHead } from '@/components/shared/screen/head';
import { ManageListSheet } from './sheets/ManageListSheet';
import { EditListNameSheet } from './sheets/EditListNameSheet';
import { ConfirmationSheet } from '@/components/shared/sheets/confirmation';
import { FavoritesActionToast } from './ui/FavoritesActionToast';
import { FavoritesProductGrid, isFavoriteProductUnavailable } from './ui/FavoritesProductGrid';
import { FavoritesEventCard } from './ui/FavoritesEventCard';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core/stores';
import {
  useFavoriteItems,
  useRemoveFavoriteItem,
  useFavoriteShopProducts,
} from '@/services/platform/data/shop';
import { useFavoriteEvents } from '@/services/platform/data/events';
import type { FavoriteListDto } from '@/services/platform/api/model';
import type { FavoritesTab } from './index';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FavoritesListDetailProps {
  list: FavoriteListDto;
  activeTab: FavoritesTab;
  onBack: () => void;
  onEditName: (name: string) => Promise<void>;
  onDeleteList: () => Promise<void>;
  /** Label + handler for the empty-state exploration shortcut, sourced from the
   *  CMS-configured link (shopListsCta / eventListsCta). Undefined hides the button. */
  exploreCta?: { label: string; onClick: () => void };
  /** Shop tab only — navigates to the product detail screen for the given product publicGuid. */
  onProductClick?: (publicGuid: string) => void;
  /** Events tab only — navigates to the event detail screen for the given event id. */
  onEventClick?: (eventId: string) => void;
  /** Called after an item is removed, so the parent can refresh the list overview
   *  (item counts, preview images). */
  onItemsChanged?: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function FavoritesListDetail({
  list,
  activeTab,
  onBack,
  onEditName,
  onDeleteList,
  exploreCta,
  onProductClick,
  onEventClick,
  onItemsChanged,
}: FavoritesListDetailProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const [isManageOpen, setManageOpen] = useState(false);
  const [isEditNameOpen, setEditNameOpen] = useState(false);
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingRemoveEventGuid, setPendingRemoveEventGuid] = useState<string | undefined>(undefined);
  const [isEventRemovedToastOpen, setEventRemovedToastOpen] = useState(false);
  const { data: items, isLoading, refetch } = useFavoriteItems(list.publicGuid);
  const { removeItem } = useRemoveFavoriteItem();

  const isShop = activeTab === 'Shop';
  const { data: shopProducts, refetch: refetchShopProducts } = useFavoriteShopProducts(isShop ? items : []);
  const { data: favoriteEvents } = useFavoriteEvents(isShop ? [] : items);
  const unavailableItemGuids = useMemo(
    () =>
      shopProducts
        .filter(isFavoriteProductUnavailable)
        .map(p => p.item.publicGuid)
        .filter((g): g is string => !!g),
    [shopProducts]
  );

  async function handleRemove(itemGuid: string | undefined) {
    if (!list.publicGuid || !itemGuid) return;
    await removeItem(list.publicGuid, itemGuid);
    await refetch();
    onItemsChanged?.();
  }

  async function handleRemoveUnavailable() {
    if (!list.publicGuid || unavailableItemGuids.length === 0) return;
    await Promise.all(unavailableItemGuids.map(itemGuid => removeItem(list.publicGuid!, itemGuid)));
    await refetch();
    await refetchShopProducts();
    onItemsChanged?.();
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      {/* List name + manage menu (rename / delete) */}
      <ScreenHead
        title={list.name ?? ''}
        onBack={onBack}
        ariaLabel={t('App.Labels.GoBack')}
        className="pt-safe-area"
        rightActions={
          <button
            type="button"
            aria-label={t('App.Shop.Collection.OpenManageList')}
            onClick={() => setManageOpen(true)}
            className="flex size-11 items-center justify-center"
          >
            <Icon name="more-horiz" width={20} height={20} className="text-jncquoi-neutral-1000" />
          </button>
        }
      />

      {/* Empty state — with a Shop/Events exploration shortcut pinned to the bottom */}
      {!isLoading && items.length === 0 && (
        <>
          {/* pt-30 = the 120px top offset the design specifies, matching the same empty-state
              convention used in payment/list.tsx and elsewhere — the message sits near the
              header rather than centred in the remaining viewport. */}
          <div className="flex flex-1 flex-col items-center gap-2 px-4 pt-30 text-center tracking-[0.5px]">
            <div className="flex size-11 shrink-0 items-center justify-center">
              <Icon name="heart" width={20} height={20} className="text-jncquoi-neutral-1000" />
            </div>
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] uppercase">
              {t('App.Shop.Favorites.ListEmptyTitle')}
            </p>
            <p className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[14px]">
              {activeTab === 'Shop'
                ? t('App.Shop.Favorites.ListEmptyDescriptionShop')
                : t('App.Shop.Favorites.ListEmptyDescriptionEvents')}
            </p>
          </div>

          {isShop && exploreCta && (
            <div className="bg-jncquoi-neutral-00 fixed inset-x-0 bottom-0 px-4 pb-12">
              <Button variant="primary-solid" size="lg" onClick={exploreCta.onClick} className="w-full">
                {exploreCta.label}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Saved items — 2-col product grid for Shop, single-column row list for Events */}
      {!isLoading && items.length > 0 && isShop && (
        <div className="px-4 pt-4 pb-30">
          <FavoritesProductGrid
            products={shopProducts}
            locale={locale}
            onRemove={handleRemove}
            onProductClick={publicGuid => publicGuid && onProductClick?.(publicGuid)}
          />
        </div>
      )}

      {!isLoading && items.length > 0 && !isShop && (
        <div className="flex flex-col gap-4 px-4 pt-4 pb-30">
          {favoriteEvents.map(favEvent => (
            <FavoritesEventCard
              key={favEvent.item.publicGuid}
              favEvent={favEvent}
              onRemove={() => setPendingRemoveEventGuid(favEvent.item.publicGuid)}
              onSelect={() => {
                const eventId = favEvent.event?.id ?? favEvent.item.itemExternalId;
                if (eventId) onEventClick?.(eventId);
              }}
            />
          ))}
        </div>
      )}

      {/* Events tab: the "Explore Events Calendar" CTA is always visible, not only on the
          empty state — unlike Shop, where it's replaced by the RemoveUnavailable bar once
          items exist. */}
      {!isShop && !isLoading && exploreCta && (
        <div className="bg-jncquoi-neutral-00 fixed inset-x-0 bottom-0 px-4 pb-12">
          <Button variant="primary-solid" size="lg" onClick={exploreCta.onClick} className="w-full">
            {exploreCta.label}
          </Button>
        </div>
      )}

      {/* Bottom bar — bulk-remove sold out / discontinued products. Always visible on the
          Shop tab once items are loaded; disabled when nothing is unavailable to remove. */}
      {isShop && !isLoading && items.length > 0 && (
        <div className="bg-jncquoi-neutral-00 border-jncquoi-neutral-400 fixed inset-x-0 bottom-0 border-t-[0.5px] px-4 pt-6 pb-10.5">
          <Button
            variant="secondary-outline"
            palette="dark"
            size="md"
            disabled={unavailableItemGuids.length === 0}
            onClick={handleRemoveUnavailable}
            className="disabled:border-jncquoi-neutral-300 w-full"
          >
            {t('App.Shop.Collection.RemoveUnavailable')}
          </Button>
        </div>
      )}

      {/* Manage menu (⋯) and the sheets it opens: edit name, delete confirmation */}
      <ManageListSheet
        open={isManageOpen}
        onOpenChange={setManageOpen}
        listName={list.name ?? undefined}
        onEditName={() => {
          setManageOpen(false);
          setEditNameOpen(true);
        }}
        onDeleteList={() => {
          setManageOpen(false);
          setDeleteConfirmOpen(true);
        }}
      />

      <EditListNameSheet
        open={isEditNameOpen}
        onOpenChange={setEditNameOpen}
        initialName={list.name ?? ''}
        onSave={async name => {
          try {
            await onEditName(name);
            setEditNameOpen(false);
          } catch {
            // Sheet stays open — EditListNameSheet re-enables its Save button.
          }
        }}
      />

      {/* silentSuccess: skip the sheet's own inline success text — the parent
          shows a FavoritesActionToast instead, matching the create-list flow */}
      <ConfirmationSheet
        open={isDeleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={onDeleteList}
        onSuccess={onBack}
        silentSuccess
        headerTitle={t('App.Labels.Confirmation')}
        title={t('App.Shop.Favorites.DeleteListTitle')}
        confirmLabel={t('App.Labels.Delete')}
        dismissLabel={t('App.Labels.Cancel')}
      />

      {/* Events-only: `pendingRemoveEventGuid` is only set in the !isShop branch above. */}
      <ConfirmationSheet
        open={!!pendingRemoveEventGuid}
        onOpenChange={open => !open && setPendingRemoveEventGuid(undefined)}
        onConfirm={() => handleRemove(pendingRemoveEventGuid)}
        onSuccess={() => setEventRemovedToastOpen(true)}
        silentSuccess
        headerTitle={t('App.Labels.Confirmation')}
        title={t('App.Shop.Favorites.DeleteEventTitle')}
        confirmLabel={t('App.Labels.Delete')}
        dismissLabel={t('App.Labels.Cancel')}
      />

      <FavoritesActionToast
        open={isEventRemovedToastOpen}
        message={t('App.Shop.Favorites.EventDeleted')}
        onDismiss={() => setEventRemovedToastOpen(false)}
        anchor="bottom"
      />
    </div>
  );
}
